import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaTags } from "react-icons/fa";
import BackMethods from "../../back_component/BackMethods";

const MAX_RESPONSE_LENGTH = 2000;

export default function ProcessComplaintModal({ isOpen, onClose, onConfirm, loading }) {
  const { backEnd_request } = BackMethods();
  const [response, setResponse] = useState("");
  const [markPublic, setMarkPublic] = useState(false);
  const [complaintCategoryId, setComplaintCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setResponse("");
    setMarkPublic(false);
    setComplaintCategoryId("");
    setFormError("");

    let cancelled = false;
    (async () => {
      setCategoriesLoading(true);
      try {
        const res = await backEnd_request.get("/ComplaintCategories/active");
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : [];
          setCategories(
            [...list].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCategories([]);
          setFormError("تعذر تحميل تصنيفات الشكاوي النشطة.");
        }
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, backEnd_request]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    const trimmed = response.trim();
    if (!complaintCategoryId) {
      setFormError("يجب اختيار تصنيف الشكوى قبل الحفظ.");
      return;
    }
    if (!trimmed) {
      setFormError("يرجى إدخال نص المعالجة.");
      return;
    }
    if (trimmed.length > MAX_RESPONSE_LENGTH) {
      setFormError(`نص المعالجة يجب ألا يتجاوز ${MAX_RESPONSE_LENGTH} حرف.`);
      return;
    }

    onConfirm({
      response: trimmed,
      markPublic,
      complaintCategoryId: Number(complaintCategoryId),
    });
  };

  return (
    <div className="modal-overlay shadow">
      <div className="modal-content-custom rounded-4 shadow-lg border-0">
        <div className="modal-header border-bottom p-3 d-flex justify-content-between align-items-center bg-light rounded-top-4">
          <h5 className="mb-0 fw-bold text-dark">معالجة الشكوى</h5>
          <button type="button" className="btn-close" onClick={onClose} disabled={loading}></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body p-4">
            <div className="mb-3">
              <label className="form-label fw-bold small text-muted" htmlFor="process-category">
                <FaTags className="ms-1" />
                تصنيف الشكوى <span className="text-danger">*</span>
              </label>
              <select
                id="process-category"
                className="form-select border-2 rounded-3"
                required
                value={complaintCategoryId}
                onChange={(e) => setComplaintCategoryId(e.target.value)}
                disabled={categoriesLoading || loading}
              >
                <option value="">
                  {categoriesLoading ? "جاري تحميل التصنيفات..." : "اختر التصنيف"}
                </option>
                {categories.map((cat) => (
                  <option key={cat.complaintCategoryId} value={cat.complaintCategoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {!categoriesLoading && categories.length === 0 ? (
                <div className="form-text text-danger">لا توجد تصنيفات نشطة متاحة حالياً.</div>
              ) : null}
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label fw-bold small text-muted mb-0" htmlFor="process-response">
                  رد الإدارة على الشكوى <span className="text-danger">*</span>
                </label>
                <span
                  className={`small ${response.length > MAX_RESPONSE_LENGTH ? "text-danger fw-bold" : "text-muted"}`}
                >
                  {response.length} / {MAX_RESPONSE_LENGTH}
                </span>
              </div>
              <textarea
                id="process-response"
                className="form-control border-2 rounded-3"
                rows="5"
                required
                maxLength={MAX_RESPONSE_LENGTH}
                value={response}
                onChange={(e) => setResponse(e.target.value.slice(0, MAX_RESPONSE_LENGTH))}
                placeholder="اكتب تفاصيل المعالجة هنا..."
                disabled={loading}
              ></textarea>
            </div>

            <div className="bg-light p-3 rounded-3 border">
              <div className="form-check form-switch d-flex align-items-center justify-content-between p-0">
                <div>
                  <label className="form-check-label fw-bold d-block">جعل الشكوى عامة</label>
                  <small className="text-muted">سيتمكن الزوار من رؤية الشكوى</small>
                </div>
                <input
                  className="form-check-input ms-0"
                  type="checkbox"
                  checked={markPublic}
                  onChange={(e) => setMarkPublic(e.target.checked)}
                  disabled={loading}
                  style={{ width: "2.5em", height: "1.25em", cursor: "pointer" }}
                />
              </div>
            </div>

            {formError ? (
              <div className="alert alert-danger py-2 px-3 small mb-0 mt-3" role="alert">
                {formError}
              </div>
            ) : null}
          </div>
          <div className="modal-footer border-top p-3 bg-light rounded-bottom-4">
            <button
              type="button"
              className="btn btn-secondary rounded-pill px-4"
              onClick={onClose}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="btn btn-success rounded-pill px-4 fw-bold"
              disabled={loading || categoriesLoading || categories.length === 0}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <FaCheckCircle className="ms-2" />
              )}
              تأكيد المعالجة
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.6); z-index: 10000; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
        .modal-content-custom { background: white; width: 520px; max-width: 95%; position: relative; animation: slideUp 0.3s ease-out; }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}
